import { Component } from '../core/smpl-components.js'
import { InfiniteColumns } from '../modules/infinite-columns.js'
import { ResizeCallback } from '../utils/resize-callback.js'

const SCROLL_LOCK_CLASS = 'is-home-scroll-locked'

/**
 * HomeScroller
 *
 * Wires the infinite column wall on the home page. Selector: `.js-home-scroller`.
 *
 * The component owns two things the engine deliberately does not:
 *
 * 1. The column count. It is declared in CSS (`--columns` in
 *    _components/_home-scroller.scss) so breakpoints stay in the stylesheet;
 *    this reads it back and hands the number to the engine.
 * 2. The page scroll lock. Applied from JS, not CSS, so a page without
 *    JavaScript still scrolls normally.
 */
export default class extends Component {
	mount() {
		this.columns = new InfiniteColumns(this.element, {
			minPerColumn: 3, // cards each column must end up with
			drift: 22, // idle speed, px/sec
			ease: 6, // lerp rate; higher catches up faster
			wheelMultiplier: 1,
			keyMultiplier: 1,
			speeds: [1, 0.86, 1.14, 0.93, 1.07], // per column, indexed modulo
			// Observer reports touch drags opposite to wheel: dragging the content
			// up is a downward scroll. Inverted by default so the two agree.
			invertTouch: true,
			// Input below this (px) does not steer the drift — a few pixels of
			// finger jitter at the end of a gesture should not reverse the wall.
			directionThreshold: 2,
		})
		this.columnCount = this.readColumnCount()

		this.columns.build(this.columnCount)
		this.columns.start()

		document.documentElement.classList.add(SCROLL_LOCK_CLASS)

		this.resizeCallback = new ResizeCallback(() => this.onResize(), {
			delay: 150,
			resizeThreshold: 10,
		})
	}

	unmount() {
		if (this.resizeCallback) {
			this.resizeCallback.destroy()
			this.resizeCallback = null
		}

		if (this.columns) {
			this.columns.destroy()
			this.columns = null
		}

		document.documentElement.classList.remove(SCROLL_LOCK_CLASS)
	}

	readColumnCount() {
		const value = parseInt(getComputedStyle(this.element).getPropertyValue('--columns'), 10)
		return Number.isFinite(value) && value > 0 ? value : 1
	}

	onResize() {
		if (!this.columns) return

		const count = this.readColumnCount()

		// A changed count means the cards have to be dealt again; anything else
		// (a height change, a font settling) only invalidates the measurements.
		if (count !== this.columnCount) {
			this.columnCount = count
			this.columns.build(count)
			return
		}

		this.columns.measure()
	}
}
